import './Profile.scss';

import classNames from 'classnames';
import { Field, useFormikContext } from 'formik';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { usePhoneOptionsContext } from '../../../context/PhoneOptionsContext';
import useAxios from '../../../hooks/useAxios';
import { WithData } from '../../../types/api';
import { ClientResource } from '../../../types/api/clients';
import {
  MunicipalityCollection,
  MunicipalityPlaceCollection,
} from '../../../types/api/municipalities';
import { PlaceStreetCollection } from '../../../types/api/places';
import { isStreetFreeInputAllowed } from '../../../utils/constants/misc';
import {
  mediumStrenthRegex,
  passwordFeedback,
  strongStrenthRegex,
} from '../../../utils/constants/passwords';
import { invalidPhoneNumberCharactersRegex } from '../../../utils/constants/phoneNumbers';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../utils/helpers/phoneNumbers';
import FieldWithErrors from '../../Forms/FieldWithErrors/FieldWithErrors';
import { FormFields } from './Profile.functions';

type Props = {
  data: ClientResource | undefined;
  isSmallerDevice: boolean;
};

function ProfileContent({ data, isSmallerDevice }: Props) {
  const { values, setFieldValue } = useFormikContext<FormFields>();

  const { t } = useTranslation();

  const passwordFieldClassName = classNames({
    'p-col-12  p-py-0': isSmallerDevice,
    'p-col-6': !isSmallerDevice,
  });

  const fieldClassName = classNames({
    'p-col-12': isSmallerDevice,
    'p-col-6': !isSmallerDevice,
  });

  const { data: municipalitiesData, isLoading: isMunicipalitiesDataLoading } =
    useAxios<WithData<MunicipalityCollection>>('/municipalities?limit=0');

  const {
    data: placesInMunicipalityData,
    reload: reloadPlacesInMunicipalityData,
    isLoading: isPlacesInMunicipalityDataLoading,
  } = useAxios<MunicipalityPlaceCollection>(
    { url: `/municipalities/${values?.address?.municipalityId}/places` },
    {
      skipWhen: !values.address?.municipalityId,
    }
  );

  const {
    data: streetsData,
    reload: reloadStreetsData,
    isLoading: isStreetsDataLoading,
  } = useAxios<WithData<PlaceStreetCollection>>(
    `/places/${values.address?.placeId}/streets?limit=0`,
    {
      skipWhen: !values.address?.placeId,
    }
  );

  const municipalitiesOptions = municipalitiesData?.data?.map((m) => ({
    label: m.name,
    value: m.id,
  }));

  const placesOptions = placesInMunicipalityData?.map((m) => ({
    label: m.place_name,
    value: m.place_id,
  }));

  const streetsOptions =
    streetsData && streetsData?.data?.length > 0
      ? streetsData?.data?.map((s) => ({ label: s.name, value: s.id }))
      : [];

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  return (
    <div className="p-fluid">
      <div className="p-d-flex  p-ai-center">
        <FieldWithErrors
          name="change_pass_switch"
          label={t('Change password')}
          className="change-pass-label"
        >
          <Field
            as={InputSwitch}
            checked={values.change_pass_switch}
            name="change_pass_switch"
            id="change_pass_switch"
            className="p-mr-4"
          />
        </FieldWithErrors>
      </div>

      {values.change_pass_switch && (
        <div className="p-grid p-mt-1">
          <div className={passwordFieldClassName}>
            <FieldWithErrors name="new_password" label={t('New Password')}>
              <Field
                as={Password}
                inputId="new_password"
                name="new_password"
                feedback
                header={passwordFeedback(t)}
                mediumRegex={mediumStrenthRegex as any}
                strongRegex={strongStrenthRegex as any}
                toggleMask
                autoComplete="off"
                promptLabel=" "
              />
            </FieldWithErrors>
          </div>

          <div className={passwordFieldClassName}>
            <FieldWithErrors
              name="confirm_password"
              label={t('Confirm Password')}
            >
              <Field
                as={Password}
                inputId="confirm_password"
                name="confirm_password"
                feedback
                header={passwordFeedback(t)}
                mediumRegex={mediumStrenthRegex as any}
                strongRegex={strongStrenthRegex as any}
                toggleMask
                autoComplete="off"
                promptLabel=" "
              />
            </FieldWithErrors>
          </div>
        </div>
      )}

      <div className="p-grid p-mt-5">
        <div className={fieldClassName}>
          <FieldWithErrors name="ime" label={t('Name')}>
            <Field name="ime" as={InputText} id="ime" />
          </FieldWithErrors>

          <FieldWithErrors name="mobilen" label={t('Mobile')}>
            <div className="p-inputgroup">
              <Field
                as={Dropdown}
                className="mobilen_region"
                style={{ padding: 0, width: '5rem' }}
                id="mobilen_region"
                name="mobilen_region"
                options={languages}
                itemTemplate={countryOptionTemplate}
                valueTemplate={selectedCountryTemplate}
                data-cy="mobilen_region"
              />
              <Field
                as={InputText}
                name="mobilen"
                style={{ width: '100%' }}
                id="mobilen"
                value={formatMobileNumber(
                  values.mobilen,
                  values.mobilen_region ||
                    getPhoneorMobileNumberRegion(values.mobilen)
                )}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setFieldValue(
                    'mobilen',
                    e.target.value?.replace(
                      invalidPhoneNumberCharactersRegex,
                      ''
                    )
                  );
                }}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="telefon" label={t('Phone')}>
            <div className="p-inputgroup">
              <Field
                as={Dropdown}
                className="telefon_region"
                style={{ padding: 0, width: '5rem' }}
                id="telefon_region"
                name="telefon_region"
                options={languages}
                itemTemplate={countryOptionTemplate}
                valueTemplate={selectedCountryTemplate}
                data-cy="telefon_region"
              />
              <Field
                as={InputText}
                name="telefon"
                id="telefon"
                style={{ width: '100%' }}
                value={formatPhoneNumber(
                  values.telefon,
                  values.telefon_region ||
                    getPhoneorMobileNumberRegion(values.telefon)
                )}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setFieldValue(
                    'telefon',
                    e.target.value?.replace(
                      invalidPhoneNumberCharactersRegex,
                      ''
                    )
                  );
                }}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="email" label={t('Email (Contact)')}>
            <Field
              as={InputText}
              name="email"
              id="email"
              value={values.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setFieldValue('email', e.target.value);
              }}
            />
          </FieldWithErrors>
        </div>

        <div className={fieldClassName}>
          <FieldWithErrors
            name="address.municipalityId"
            label={t('Municipality')}
          >
            <Field
              as={Dropdown}
              name="address.municipalityId"
              inputId="address.municipalityId"
              options={municipalitiesOptions}
              filter
              filterPlaceholder={t('Search')}
              placeholder={isMunicipalitiesDataLoading ? t('Loading...') : null}
              onChange={(e: DropdownChangeParams) => {
                setFieldValue('address.municipalityId', e.value);

                let ime = municipalitiesOptions?.find(
                  (m) => m.value === e.value
                );
                setFieldValue('address.municipalityName', ime?.label);

                reloadPlacesInMunicipalityData({
                  url: `municipalities/${e.value}/places`,
                });

                setFieldValue('address.placeId', '');
                setFieldValue('address.placeName', '');
                setFieldValue('address.streetId', '');
                setFieldValue('address.streetName', '');
                setFieldValue('address.housenumber', '');
                setFieldValue('address.entrance', '');
                setFieldValue('address.apartment', '');
              }}
              className="data-cy-municipality"
            />
          </FieldWithErrors>

          <FieldWithErrors name="address.placeId" label={t('Place')}>
            <Field
              as={Dropdown}
              name="address.placeId"
              inputId="address.placeId"
              disabled={
                !values.address.municipalityId ||
                isPlacesInMunicipalityDataLoading
              }
              options={placesOptions}
              filter
              filterPlaceholder={t('Search')}
              placeholder={
                isPlacesInMunicipalityDataLoading ? t('Loading...') : null
              }
              onChange={(e: DropdownChangeParams) => {
                if (e.value) {
                  const place = placesInMunicipalityData?.find((p) => {
                    return p.place_id === e.value;
                  });

                  setFieldValue('address.placeId', e.value);
                  setFieldValue('address.placeName', place?.place_name);

                  reloadStreetsData({
                    url: `places/${e.value}/streets?limit=0`,
                  });
                } else {
                  setFieldValue('address.placeId', '');
                  setFieldValue('address.placeName', '');
                  setFieldValue('address.streetId', '');
                  setFieldValue('address.streetName', '');
                  setFieldValue('address.housenumber', '');
                  setFieldValue('address.entrance', '');
                  setFieldValue('address.apartment', '');
                }
              }}
              className="data-cy-place"
            />
          </FieldWithErrors>

          {isStreetFreeInputAllowed ? (
            <FieldWithErrors
              name="address"
              label={isStreetFreeInputAllowed ? t('Address') : t('Street')}
              includeErrorsFor={[
                'address.streetId',
                'adresa',
                'address.housenumber',
              ]}
            >
              <Field
                as={InputText}
                name="adresa"
                id="adresa"
                disabled={!values.address.placeId}
                maxLength="256"
              />
            </FieldWithErrors>
          ) : (
            <>
              <FieldWithErrors name="address.streetId" label={t('Street')}>
                <Field
                  as={Dropdown}
                  name="address.streetId"
                  inputId="address.streetId"
                  disabled={
                    !values.address.municipalityId ||
                    !values.address.placeId ||
                    isStreetsDataLoading
                  }
                  options={streetsOptions}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isStreetsDataLoading ? t('Loading...') : null}
                  onChange={(e: DropdownChangeParams) => {
                    const street = streetsData?.data?.find(
                      (s) => s.id === e.value
                    );

                    setFieldValue('address.streetId', e.value);
                    setFieldValue('address.streetName', street?.name);
                  }}
                  className="data-cy-street"
                />
              </FieldWithErrors>
              <div className="street-numbers">
                <FieldWithErrors
                  name="address.housenumber"
                  label={t('Street No.')}
                >
                  <Field
                    disabled={!values.address.streetId}
                    as={InputText}
                    id="address.housenumber"
                    name="address.housenumber"
                    data-cy="street_no"
                  />
                </FieldWithErrors>
                <FieldWithErrors
                  name="address.entrance"
                  label={t('Entrance No.')}
                >
                  <Field
                    disabled={!values.address.streetId}
                    as={InputText}
                    id="address.entrance"
                    name="address.entrance"
                    data-cy="street_no"
                  />
                </FieldWithErrors>
                <FieldWithErrors name="address.apartment" label={t('Flat No.')}>
                  <Field
                    disabled={!values.address.streetId}
                    as={InputText}
                    id="address.apartment"
                    name="address.apartment"
                    data-cy="street_no"
                  />
                </FieldWithErrors>
              </div>
            </>
          )}
        </div>
      </div>

      {data?.pravno_lice === 1 && (
        <div className="p-grid p-mt-4">
          <div className={`p-py-0 ${fieldClassName}`}>
            <FieldWithErrors name="danocen_broj" label={t('Tax')}>
              <Field name="danocen_broj" as={InputText} disabled />
            </FieldWithErrors>
          </div>

          <div className={`p-py-0 ${fieldClassName}`}>
            <FieldWithErrors name="smetka" label={t('Bank Account')}>
              <Field name="smetka" as={InputText} disabled />
            </FieldWithErrors>
          </div>

          <div className={`p-py-0 ${fieldClassName}`}>
            <FieldWithErrors name="kontakt_lice" label={t('Contact person')}>
              <Field name="kontakt_lice" as={InputText} />
            </FieldWithErrors>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileContent;
